import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Tabs,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  message,
  Tag,
  Grid,
} from 'antd';
import {
  BarChartOutlined,
  CalendarOutlined,
  DollarOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import adsService, { AdAccount, FilterParams } from '../../services/ads.service';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface WeeklyData {
  _id: number;
  weekStart: string;
  weekEnd: string;
  totalSpend: number;
  totalResults: number;
  avgCostPerResult: number;
}

interface MonthlyData {
  _id: { year: number; month: number };
  totalSpend: number;
  totalResults: number;
  avgCostPerResult: number;
}

const AdReports: React.FC = () => {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [weeklyData, setWeeklyData] = useState<WeeklyData[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('weekly');

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Fetch Ad Accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data = await adsService.getAdAccounts();
        setAccounts(data);
        
        // Try to get from localStorage
        const savedAccount = localStorage.getItem('selected_ad_account');
        if (savedAccount && data.find(a => a._id === savedAccount)) {
          setSelectedAccount(savedAccount);
        } else if (data.length > 0) {
          setSelectedAccount(data[0]._id);
        }
      } catch (error) {
        message.error('Không thể tải danh sách tài khoản');
      }
    };
    fetchAccounts();
  }, []);

  const handleAccountChange = (value: string) => {
    setSelectedAccount(value);
    localStorage.setItem('selected_ad_account', value);
  };

  // Fetch Reports
  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedAccount) return;

      setLoading(true);
      try {
        const filter: FilterParams = { adAccountId: selectedAccount };
        const [weekly, monthly] = await Promise.all([
          adsService.getWeeklyReport(filter),
          adsService.getMonthlyReport(filter),
        ]);
        setWeeklyData(weekly);
        setMonthlyData(monthly);
      } catch (error) {
        message.error('Không thể tải báo cáo');
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [selectedAccount]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value);
  };

  const getMonthName = (month: number) => {
    const months = [
      'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4',
      'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8',
      'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12',
    ];
    return months[month - 1];
  };

  // Calculate totals
  const weeklyTotals = weeklyData.reduce(
    (acc, w) => ({
      spend: acc.spend + w.totalSpend,
      results: acc.results + w.totalResults,
    }),
    { spend: 0, results: 0 }
  );

  const monthlyTotals = monthlyData.reduce(
    (acc, m) => ({
      spend: acc.spend + (m.totalSpend || 0),
      results: acc.results + (m.totalResults || 0),
    }),
    { spend: 0, results: 0 }
  );

  const weeklyColumns: ColumnsType<WeeklyData> = [
    {
      title: 'Tuần',
      key: 'week',
      width: isMobile ? 180 : 200,
      render: (_, record) => (
        <Space size={4}>
          <CalendarOutlined style={{ fontSize: isMobile ? 12 : 14 }} />
          <Text style={{ fontSize: isMobile ? 12 : 14 }}>
            {new Date(record.weekStart).toLocaleDateString(isMobile ? 'vi' : 'vi-VN')} -{' '}
            {new Date(record.weekEnd).toLocaleDateString(isMobile ? 'vi' : 'vi-VN')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'K.Quả',
      dataIndex: 'totalResults',
      key: 'totalResults',
      align: 'right',
      width: 80,
      render: (value) => <Tag color="blue" style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>{formatNumber(value)}</Tag>,
    },
    {
      title: 'Chi phí/KQ',
      dataIndex: 'avgCostPerResult',
      key: 'avgCostPerResult',
      align: 'right',
      width: 120,
      render: (value) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{formatCurrency(value)}</span>,
    },
    {
      title: 'Chi tiêu',
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      align: 'right',
      width: 120,
      render: (value) => (
        <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: 'Chi tiêu (VAT 10%)',
      dataIndex: 'totalSpendAfterTax',
      key: 'totalSpendAfterTax',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }}>
          {formatCurrency(record.totalSpend * 1.1)}
        </Text>
      ),
    },
  ];

  const monthlyColumns: ColumnsType<MonthlyData> = [
    {
      title: 'Tháng',
      key: 'month',
      width: isMobile ? 120 : 150,
      render: (_, record) => (
        <Space size={4}>
          <CalendarOutlined style={{ fontSize: isMobile ? 12 : 14 }} />
          <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
            {getMonthName(record._id.month)} {record._id.year}
          </Text>
        </Space>
      ),
    },
    {
      title: 'K.Quả',
      dataIndex: 'totalResults',
      key: 'totalResults',
      align: 'right',
      width: 80,
      render: (value) => <Tag color="blue" style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>{formatNumber(value)}</Tag>,
    },
    {
      title: 'Chi phí/KQ',
      dataIndex: 'avgCostPerResult',
      key: 'avgCostPerResult',
      align: 'right',
      width: 120,
      render: (value) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{formatCurrency(value)}</span>,
    },
    {
      title: 'Chi tiêu',
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      align: 'right',
      width: 120,
      render: (value) => (
        <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: 'Chi tiêu (VAT 10%)',
      dataIndex: 'totalSpendAfterTax',
      key: 'totalSpendAfterTax',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }}>
          {formatCurrency(record.totalSpend * 1.1)}
        </Text>
      ),
    },
  ];

  const totals = activeTab === 'weekly' ? weeklyTotals : monthlyTotals;

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <Space size={isMobile ? 8 : 12}>
            <BarChartOutlined style={{ fontSize: isMobile ? 20 : 24, color: '#1890ff' }} />
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              Báo cáo quảng cáo
            </Title>
          </Space>
        }
        extra={
          <Select
            style={{ width: isMobile ? 150 : 250 }}
            placeholder="Chọn tài khoản"
            size={isMobile ? 'small' : 'middle'}
            value={selectedAccount}
            onChange={handleAccountChange}
            options={accounts.map((a) => ({ label: a.name, value: a._id }))}
          />
        }
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        {/* Summary Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Tổng chi tiêu</Text>}
                value={totals.spend}
                precision={0}
                prefix={<DollarOutlined />}
                suffix="₫"
                valueStyle={{ color: '#fff', fontSize: isMobile ? 18 : 20 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Tổng (VAT 10%)</Text>}
                value={totals.spend * 1.1}
                precision={0}
                prefix={<DollarOutlined />}
                suffix="₫"
                valueStyle={{ color: '#fff', fontSize: isMobile ? 18 : 20 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Kết quả</Text>}
                value={totals.results}
                prefix={<RiseOutlined />}
                valueStyle={{ color: '#fff', fontSize: isMobile ? 18 : 20 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>AVG / CQ</Text>}
                value={totals.results > 0 ? totals.spend / totals.results : 0}
                precision={0}
                suffix="₫"
                valueStyle={{ color: '#fff', fontSize: isMobile ? 18 : 20 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Tabs for Weekly/Monthly */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size={isMobile ? 'small' : 'middle'}
          items={[
            {
              key: 'weekly',
              label: (
                <span style={{ fontSize: isMobile ? 12 : 14 }}>
                  <CalendarOutlined /> Tuần
                </span>
              ),
              children: (
                <Table
                  columns={weeklyColumns}
                  dataSource={weeklyData}
                  rowKey={(record) => `${record._id}`}
                  loading={loading}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: 'Chưa có báo cáo' }}
                />
              ),
            },
            {
              key: 'monthly',
              label: (
                <span style={{ fontSize: isMobile ? 12 : 14 }}>
                  <CalendarOutlined /> Tháng
                </span>
              ),
              children: (
                <Table
                  columns={monthlyColumns}
                  dataSource={monthlyData}
                  rowKey={(record) => `${record._id.year}-${record._id.month}`}
                  loading={loading}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: 'Chưa có báo cáo' }}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AdReports;
